import * as THREE from 'three'
import { RunConfig } from '../../config/runs'
import { getElevation } from '../terrain/SlopeTerrain'

/**
 * Creates simple blocky pine trees lining both sides of a run.
 * Roblox-style: cone on top of a cylinder trunk.
 */

function createTree(height: number): THREE.Group {
  const tree = new THREE.Group()

  const trunk = new THREE.Mesh(
    new THREE.CylinderGeometry(0.15, 0.2, height * 0.3, 6),
    new THREE.MeshStandardMaterial({ color: 0x5c3a1e, roughness: 0.8 })
  )
  trunk.position.y = height * 0.15
  trunk.castShadow = true
  tree.add(trunk)

  const foliageMat = new THREE.MeshStandardMaterial({ color: 0x2d5a27, roughness: 0.7 })
  const layers = 3
  for (let i = 0; i < layers; i++) {
    const radius = (0.8 - i * 0.15) * (height / 3)
    const coneHeight = height * 0.35
    const cone = new THREE.Mesh(
      new THREE.ConeGeometry(radius, coneHeight, 7),
      foliageMat
    )
    cone.position.y = height * 0.3 + i * coneHeight * 0.55
    cone.castShadow = true
    tree.add(cone)
  }

  return tree
}

function seededRandom(seed: number): number {
  const n = Math.sin(seed * 127.1) * 43758.5453
  return n - Math.floor(n)
}

export interface SideTreeData {
  position: THREE.Vector3
  radius: number
}

export function createTreeLines(config: RunConfig): { group: THREE.Group; colliders: SideTreeData[] } {
  const trees = new THREE.Group()
  const colliders: SideTreeData[] = []

  // Trees fill the wider terrain — both lining the run and in the off-piste areas
  const treeCount = Math.floor(config.length / 3)
  const margin = config.width / 2 + 2

  for (let i = 0; i < treeCount; i++) {
    const seed = i * 7.3
    const side = i % 2 === 0 ? -1 : 1
    // Spread trees across the full terrain width (3x run width)
    const xOffset = margin + seededRandom(seed + 1) * config.width * 1.2
    const x = side * xOffset
    const z = -config.length / 2 + (i / treeCount) * config.length + seededRandom(seed + 2) * 10
    const height = 2.5 + seededRandom(seed + 3) * 2

    const tree = createTree(height)
    const groundY = getElevation(x, z)
    tree.position.set(x, groundY, z)
    trees.add(tree)

    colliders.push({
      position: new THREE.Vector3(x, groundY, z),
      radius: 0.6,
    })
  }

  return { group: trees, colliders }
}
